/**
 * OTA (Over-The-Air) Update Service
 * Handles firmware updates via BLE
 */

import { BLEError, BLEErrorCode } from './types'

// OTA Characteristic UUIDs
export const OTA_CONTROL_UUID = 'a0b1c2d3-e4f5-6789-abcd-ef0123456780'
export const OTA_DATA_UUID = 'a0b1c2d3-e4f5-6789-abcd-ef0123456781'
export const OTA_STATUS_UUID = 'a0b1c2d3-e4f5-6789-abcd-ef0123456782'

// OTA Commands
export const OTA_CMD_START = 0x01
export const OTA_CMD_COMPLETE = 0x02
export const OTA_CMD_ABORT = 0x03

// OTA States
export enum OTAState {
  IDLE = 0,
  STARTING = 1,
  RECEIVING = 2,
  VALIDATING = 3,
  COMPLETE = 4,
  ERROR = 5
}

// OTA Status
export interface OTAStatus {
  state: OTAState
  progress: number // 0-100
  error: number
}

export class OTAService {
  private controlChar: BluetoothRemoteGATTCharacteristic | null = null
  private dataChar: BluetoothRemoteGATTCharacteristic | null = null
  private statusChar: BluetoothRemoteGATTCharacteristic | null = null

  private chunkSize = 512 // BLE MTU size

  /**
   * Initialize OTA service with characteristics
   */
  async init(service: BluetoothRemoteGATTService): Promise<void> {
    try {
      this.controlChar = await service.getCharacteristic(OTA_CONTROL_UUID)
      this.dataChar = await service.getCharacteristic(OTA_DATA_UUID)
      this.statusChar = await service.getCharacteristic(OTA_STATUS_UUID)
    } catch (error) {
      throw new BLEError('Failed to initialize OTA service', BLEErrorCode.SERVICE_NOT_FOUND, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Subscribe to OTA status updates
   */
  async subscribeToStatus(callback: (status: OTAStatus) => void): Promise<void> {
    if (!this.statusChar) {
      throw new BLEError('OTA status characteristic not initialized', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    try {
      await this.statusChar.startNotifications()
      this.statusChar.addEventListener('characteristicvaluechanged', (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic
        const value = target.value
        if (value && value.byteLength >= 3) {
          const data = new Uint8Array(value.buffer)
          const status = {
            state: data[0] as OTAState,
            progress: data[1],
            error: data[2]
          }
          console.log(`OTA Status Update: state=${status.state}, progress=${status.progress}%, error=${status.error}`)
          callback(status)
        }
      })
    } catch (error) {
      throw new BLEError('Failed to subscribe to OTA status', BLEErrorCode.NOTIFICATION_FAILED, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Start OTA update
   */
  async startUpdate(firmwareSize: number): Promise<void> {
    if (!this.controlChar) {
      throw new BLEError('OTA control characteristic not initialized', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    try {
      const buffer = new ArrayBuffer(5)
      const view = new DataView(buffer)
      view.setUint8(0, OTA_CMD_START)
      view.setUint32(1, firmwareSize, true) // little-endian

      await this.controlChar.writeValue(buffer)
      console.log('OTA start command sent, firmware size:', firmwareSize)
    } catch (error) {
      throw new BLEError('Failed to start OTA update', BLEErrorCode.WRITE_FAILED, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Send firmware data chunk
   */
  async sendChunk(data: Uint8Array): Promise<void> {
    if (!this.dataChar) {
      throw new BLEError('OTA data characteristic not initialized', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    if (data.length > this.chunkSize) {
      throw new BLEError(`Chunk size ${data.length} exceeds maximum ${this.chunkSize}`, BLEErrorCode.WRITE_FAILED)
    }

    try {
      await this.dataChar.writeValue(new Uint8Array(data))
    } catch (error) {
      throw new BLEError('Failed to send OTA data chunk', BLEErrorCode.WRITE_FAILED, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Complete OTA update
   */
  async completeUpdate(): Promise<void> {
    if (!this.controlChar) {
      throw new BLEError('OTA control characteristic not initialized', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    try {
      const buffer = new ArrayBuffer(5)
      const view = new DataView(buffer)
      view.setUint8(0, OTA_CMD_COMPLETE)
      view.setUint32(1, 0, true) // size not needed for complete

      await this.controlChar.writeValue(buffer)
      console.log('OTA complete command sent')
    } catch (error) {
      throw new BLEError('Failed to complete OTA update', BLEErrorCode.WRITE_FAILED, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Abort OTA update
   */
  async abortUpdate(): Promise<void> {
    if (!this.controlChar) {
      throw new BLEError('OTA control characteristic not initialized', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    try {
      const buffer = new ArrayBuffer(5)
      const view = new DataView(buffer)
      view.setUint8(0, OTA_CMD_ABORT)
      view.setUint32(1, 0, true)

      await this.controlChar.writeValue(buffer)
      console.log('OTA abort command sent')
    } catch (error) {
      throw new BLEError('Failed to abort OTA update', BLEErrorCode.WRITE_FAILED, error instanceof Error ? error : new Error(String(error)))
    }
  }

  /**
   * Upload firmware file
   */
  async uploadFirmware(
    file: File,
    onProgress?: (percent: number, bytesTransferred: number, totalBytes: number) => void
  ): Promise<void> {
    console.log('Starting firmware upload:', file.name, 'Size:', file.size)

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer()
    const firmware = new Uint8Array(arrayBuffer)

    // Start OTA
    await this.startUpdate(firmware.length)

    // Wait for ESP-IDF to initialize (esp_ota_begin, partition setup, etc.)
    // Device now has circular buffer to queue chunks during initialization
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Send firmware in chunks
    let offset = 0
    while (offset < firmware.length) {
      const remaining = firmware.length - offset
      const chunkLength = Math.min(this.chunkSize, remaining)
      const chunk = firmware.slice(offset, offset + chunkLength)

      await this.sendChunk(chunk)

      offset += chunkLength

      // Report progress
      const percent = Math.round((offset / firmware.length) * 100)
      if (onProgress) {
        onProgress(percent, offset, firmware.length)
      }

      // Delay between chunks to avoid overwhelming the device
      // ESP-IDF OTA processing requires slightly longer delay than Arduino Update (was 5ms)
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    console.log('Firmware transfer complete, waiting for validation...')

    // Wait a bit before completing
    await new Promise(resolve => setTimeout(resolve, 500))

    // Complete OTA
    await this.completeUpdate()

    console.log('OTA update initiated successfully')
  }

  /**
   * Get chunk size
   */
  getChunkSize(): number {
    return this.chunkSize
  }

  /**
   * Set chunk size (for testing or optimization)
   */
  setChunkSize(size: number): void {
    if (size > 0 && size <= 512) {
      this.chunkSize = size
    }
  }
}
